import { inject, Injectable } from '@angular/core';
import { ApiAssetCategoriesAdapterService } from '@fullyops/data/asset-categories/api-adapter.service';
import {
  AssetCategory,
  AssetCategoryInput,
} from '@fullyops/data/asset-categories/asset-category';
import { createStore } from '@ngneat/elf';
import {
  withEntities,
  selectAllEntities,
  setEntities,
  deleteAllEntities,
  addEntities,
  deleteEntities,
  upsertEntities,
} from '@ngneat/elf-entities';
import { map, Observable, shareReplay } from 'rxjs';
import { v4 as getUuid } from 'uuid';

@Injectable({ providedIn: 'root' })
export class AssetCategoriesService {
  private api = inject(ApiAssetCategoriesAdapterService);

  private store = createStore(
    { name: 'asset-categories' },
    withEntities<AssetCategory>({ idKey: 'id' }),
  );

  categories$ = this.store.pipe(selectAllEntities());

  refresh(): void {
    this.api.getAll().subscribe((res) => this.store.update(setEntities(res)));
  }

  clear(): void {
    this.store.update(deleteAllEntities());
  }

  /** Emits the new category's id once it is created and listed; callers need not subscribe. */
  create(cat: AssetCategoryInput): Observable<string> {
    const provisionalId = getUuid();
    this.store.update(addEntities({ ...cat, id: provisionalId, inUse: false }));
    const created = this.api.create(cat).pipe(shareReplay(1));
    created.subscribe({
      next: (res) => {
        this.store.update(
          deleteEntities(provisionalId),
          upsertEntities({ ...cat, id: res.id, inUse: false }),
        );
      },
      error: () => {
        this.store.update(deleteEntities(provisionalId));
      },
    });
    return created.pipe(map((res) => res.id));
  }

  delete(id: string): void {
    const orig = this.store.getValue().entities[id];
    this.store.update(deleteEntities(id));
    this.api.delete(id).subscribe({
      error: () => {
        this.store.update(addEntities(orig));
      },
    });
  }
}
